/**
 * Admin Tenant Communications Page
 * 
 * Admin tool for managing tenant broadcast campaigns.
 * Features:
 * - Campaign list with status badges
 * - 3-step campaign creation wizard
 * - Campaign detail/results view
 * - CSV export
 * - Segments view (read-only)
 * - Suppression view (read-only)
 * 
 * Uses existing backend endpoints under /api/admin/tenant-comms/
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../components/ui/sheet";
import { toast } from "sonner";
import {
  Megaphone,
  Plus,
  Eye,
  ArrowClockwise,
  CaretLeft,
  CaretRight,
  PaperPlaneTilt,
  CheckCircle,
  Warning,
  Clock,
  XCircle,
  Prohibit,
  UserMinus,
  Download,
  Trash,
  Users,
  Envelope,
  Tag,
  Robot,
  ArrowRight,
  ArrowLeft,
  Play,
  TestTube,
  X,
  Info,
  CalendarBlank,
  Buildings,
  CreditCard,
  UserCircle,
  ListBullets,
  Funnel,
  TextBolder,
  TextItalic,
  TextUnderline,
  ListNumbers,
  Link,
  ArrowUUpLeft,
  ArrowUUpRight,
  TextStrikethrough,
  Code,
  FileText,
} from "phosphor-react";
import { AutomationsTab } from "./AutomationsTab";
import { TemplatesTab } from "./TemplatesTab";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Helper for auth fetch
const authFetch = async (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
};

// Status badge config
const STATUS_CONFIG = {
  draft: { icon: Clock, color: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30", label: "Draft" },
  queued: { icon: Clock, color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", label: "Queued" },
  sending: { icon: Play, color: "bg-blue-500/20 text-blue-400 border-blue-500/30", label: "Sending" },
  sent: { icon: CheckCircle, color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", label: "Sent" },
  canceled: { icon: XCircle, color: "bg-orange-500/20 text-orange-400 border-orange-500/30", label: "Canceled" },
  failed: { icon: Warning, color: "bg-red-500/20 text-red-400 border-red-500/30", label: "Failed" },
};

// Category badge config
const CATEGORY_CONFIG = {
  Operational: { color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  Product: { color: "bg-purple-500/20 text-purple-400 border-purple-500/30" },
  Marketing: { color: "bg-pink-500/20 text-pink-400 border-pink-500/30" },
};

// Supported segments (matches backend)
const SUPPORTED_SEGMENTS = [
  { value: "all_tenants", label: "All Tenants", description: "All active and suspended tenants", icon: Buildings },
  { value: "has_subscription", label: "Paid Subscribers", description: "Tenants with active paid subscription", icon: CreditCard },
  { value: "free_tier", label: "Free Tier", description: "Tenants without paid subscription", icon: UserCircle },
  { value: "created_before", label: "Created Before", description: "Tenants created before a specific date", icon: CalendarBlank, requiresDate: "created_before_date" },
  { value: "created_after", label: "Created After", description: "Tenants created after a specific date", icon: CalendarBlank, requiresDate: "created_after_date" },
];

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

// ============================================================================
// CAMPAIGNS LIST TAB
// ============================================================================

function CampaignsListTab({ onViewCampaign, onCreateCampaign }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const limit = 20;

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const skip = (page - 1) * limit;
      let url = `${API}/admin/tenant-comms/campaigns?limit=${limit}&skip=${skip}`;
      if (statusFilter) {
        url += `&status=${statusFilter}`;
      }
      const res = await authFetch(url);
      if (res.ok) {
        const data = await res.json();
        setCampaigns(data.items || []);
        setTotal(data.total || 0);
      } else {
        toast.error("Failed to load campaigns");
      }
    } catch (err) {
      toast.error("Error loading campaigns");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleCancel = async (campaignId, e) => {
    e.stopPropagation();
    if (!window.confirm("Cancel this campaign? Already-sent emails cannot be recalled.")) return;
    
    try {
      const res = await authFetch(`${API}/admin/tenant-comms/campaigns/${campaignId}/cancel`, {
        method: "POST",
      });
      if (res.ok) {
        toast.success("Campaign canceled");
        fetchCampaigns();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to cancel campaign");
      }
    } catch (err) {
      toast.error("Error canceling campaign");
    }
  };

  const handleDelete = async (campaignId, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this campaign? This cannot be undone.")) return;
    
    try {
      const res = await authFetch(`${API}/admin/tenant-comms/campaigns/${campaignId}`, {
        method: "DELETE",
      });
      if (res.ok) {
        toast.success("Campaign deleted");
        fetchCampaigns();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to delete campaign");
      }
    } catch (err) {
      toast.error("Error deleting campaign");
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-white">Campaigns</h2>
          <p className="text-sm text-zinc-400">Manage tenant broadcast campaigns</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter || "__all__"} onValueChange={(v) => { setStatusFilter(v === "__all__" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-[140px] bg-zinc-800 border-zinc-700 text-zinc-100">
              <SelectValue placeholder="All statuses" className="text-zinc-400" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="queued">Queued</SelectItem>
              <SelectItem value="sending">Sending</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="canceled">Canceled</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchCampaigns} variant="outline" size="sm" className="border-zinc-700">
            <ArrowClockwise size={16} className="mr-1" /> Refresh
          </Button>
          <Button onClick={onCreateCampaign} size="sm" className="bg-[#FF6600] hover:bg-[#FF6600]/90">
            <Plus size={16} className="mr-1" /> New Campaign
          </Button>
        </div>
      </div>

      {/* Table */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-zinc-400">Loading campaigns...</div>
          ) : campaigns.length === 0 ? (
            <div className="p-8 text-center text-zinc-400">
              <Megaphone size={48} className="mx-auto mb-4 opacity-50" />
              <p>No campaigns found</p>
              <p className="text-sm mt-2">Create your first campaign to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-800 text-zinc-400">
                    <th className="text-left p-3 font-medium">Created</th>
                    <th className="text-left p-3 font-medium">Name</th>
                    <th className="text-left p-3 font-medium">Category</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-right p-3 font-medium">Est.</th>
                    <th className="text-right p-3 font-medium">Sent</th>
                    <th className="text-right p-3 font-medium">Failed</th>
                    <th className="text-right p-3 font-medium">Suppressed</th>
                    <th className="text-right p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => {
                    const StatusIcon = STATUS_CONFIG[c.status]?.icon || Clock;
                    const counts = c.resolved_counts || {};
                    return (
                      <tr
                        key={c.campaign_id}
                        onClick={() => onViewCampaign(c.campaign_id)}
                        className="border-b border-zinc-800/50 hover:bg-zinc-800/50 cursor-pointer transition-colors"
                        data-testid={`campaign-row-${c.campaign_id}`}
                      >
                        <td className="p-3 text-zinc-400">{formatDate(c.created_at)}</td>
                        <td className="p-3 text-white font-medium">{c.name || "(Untitled)"}</td>
                        <td className="p-3">
                          <Badge variant="outline" className={`${CATEGORY_CONFIG[c.category]?.color || ""} text-xs`}>
                            {c.category}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className={`${STATUS_CONFIG[c.status]?.color || ""} text-xs`}>
                            <StatusIcon size={12} className="mr-1" />
                            {STATUS_CONFIG[c.status]?.label || c.status}
                          </Badge>
                        </td>
                        <td className="p-3 text-right text-zinc-300">{counts.estimated || 0}</td>
                        <td className="p-3 text-right text-emerald-400">{counts.delivered || counts.attempted || 0}</td>
                        <td className="p-3 text-right text-red-400">{counts.failed || 0}</td>
                        <td className="p-3 text-right text-zinc-400">{counts.suppressed || 0}</td>
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onViewCampaign(c.campaign_id)}
                              className="text-zinc-400 hover:text-white h-8 w-8 p-0"
                              title="View details"
                            >
                              <Eye size={16} />
                            </Button>
                            {(c.status === "queued" || c.status === "sending") && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => handleCancel(c.campaign_id, e)}
                                className="text-orange-400 hover:text-orange-300 h-8 w-8 p-0"
                                title="Cancel"
                              >
                                <X size={16} />
                              </Button>
                            )}
                            {(c.status === "draft" || c.status === "failed" || c.status === "canceled") && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => handleDelete(c.campaign_id, e)}
                                className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                                title="Delete"
                              >
                                <Trash size={16} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-zinc-400">
            Showing {((page - 1) * limit) + 1} - {Math.min(page * limit, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="border-zinc-700"
            >
              <CaretLeft size={16} />
            </Button>
            <span className="text-sm text-zinc-400">Page {page} of {totalPages}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="border-zinc-700"
            >
              <CaretRight size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// CREATE CAMPAIGN WIZARD
// ============================================================================

function CreateCampaignWizard({ onComplete, onCancel }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sending, setSending] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [showTestDialog, setShowTestDialog] = useState(false);
  
  // Validation errors state
  const [errors, setErrors] = useState({});
  
  // Rich text editor mode
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const editorRef = useRef(null);
  // Ref to prevent React re-renders from wiping formatting changes made by execCommand
  const isFormattingRef = useRef(false);

  // Campaign data
  const [campaign, setCampaign] = useState({
    name: "",
    category: "Operational",
    audience: {
      segment: { segment: "all_tenants" },
      filters: {},
    },
    compose: {
      subject: "",
      body_html: "",
      unsubscribe_placeholder_present: false,
    },
  });

  // Preview data
  const [preview, setPreview] = useState(null);

  // Created campaign ID (after draft creation)
  const [campaignId, setCampaignId] = useState(null);

  // Template selection state
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Fetch available templates when Step 2 loads
  useEffect(() => {
    if (step === 2 && availableTemplates.length === 0) {
      fetchAvailableTemplates();
    }
  }, [step]);

  const fetchAvailableTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await authFetch(`${API}/admin/tenant-comms/templates/active/list?template_type=campaign`);
      if (res.ok) {
        const data = await res.json();
        setAvailableTemplates(data.templates || []);
      }
    } catch (err) {
      console.error("Failed to fetch templates:", err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const applyTemplate = async (template) => {
    try {
      const res = await authFetch(`${API}/admin/tenant-comms/templates/${template.template_id}`);
      if (res.ok) {
        const data = await res.json();
        const tmpl = data.template;
        setCampaign((prev) => ({
          ...prev,
          compose: {
            ...prev.compose,
            subject: tmpl.subject || "",
            body_html: tmpl.html_body || "",
          },
        }));
        toast.success(`Template "${template.name}" applied`);
        setShowTemplateSelector(false);
      } else {
        toast.error("Failed to load template");
      }
    } catch (err) {
      console.error("Failed to apply template:", err);
      toast.error("Failed to apply template");
    }
  };

  // Update segment
  const updateSegment = (field, value) => {
    setCampaign((prev) => ({
      ...prev,
      audience: {
        ...prev.audience,
        segment: {
          ...prev.audience.segment,
          [field]: value,
        },
      },
    }));
  };

  // Update filters
  const updateFilters = (field, value) => {
    setCampaign((prev) => ({
      ...prev,
      audience: {
        ...prev.audience,
        filters: {
          ...prev.audience.filters,
          [field]: value || undefined,
        },
      },
    }));
  };

  // Update compose
  const updateCompose = (field, value) => {
    setCampaign((prev) => ({
      ...prev,
      compose: {
        ...prev.compose,
        [field]: value,
      },
    }));
  };

  // Check for unsubscribe placeholder in body
  useEffect(() => {
    const bodyLower = campaign.compose.body_html.toLowerCase();
    const hasUnsub = bodyLower.includes("{{unsubscribe") || bodyLower.includes("{unsubscribe");
    setCampaign((prev) => ({
      ...prev,
      compose: {
        ...prev.compose,
        unsubscribe_placeholder_present: hasUnsub,
      },
    }));
  }, [campaign.compose.body_html]);

  // Clear field error when user types
  const clearError = (field) => {
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  // Helper to check if HTML has meaningful content (not just empty tags)
  const isMeaningfulHtml = (html) => {
    if (!html) return false;
    // Remove all tags, &nbsp;, and whitespace
    const textContent = html
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, "")
      .trim();
    return textContent.length > 0;
  };

  // Strip script tags for safety
  const sanitizeHtml = (html) => {
    if (!html) return "";
    return html.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "");
  };

  // Rich text editor commands
  const execCommand = (command, value = null) => {
    if (!editorRef.current) return;
    
    // Set formatting flag to prevent useEffect from overwriting changes
    isFormattingRef.current = true;
    
    // Focus the editor first - required for execCommand to work
    editorRef.current.focus();
    
    // Ensure there's a selection in the editor
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !editorRef.current.contains(selection.anchorNode)) {
      // No selection in editor - place cursor at end
      const range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false); // false = collapse to end
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
    
    // Execute the command
    document.execCommand(command, false, value);
    
    // Get the updated HTML AFTER execCommand has modified the DOM
    const newHtml = sanitizeHtml(editorRef.current.innerHTML);
    
    // Update React state with the new HTML
    // Use setTimeout to ensure this happens after the DOM is fully updated
    setTimeout(() => {
      setCampaign(prev => ({
        ...prev,
        compose: {
          ...prev.compose,
          body_html: newHtml
        }
      }));
      clearError("body_html");
      clearError("unsubscribe");
      // Reset formatting flag after state update
      isFormattingRef.current = false;
    }, 0);
  };

  const handleEditorInput = () => {
    if (editorRef.current) {
      const sanitized = sanitizeHtml(editorRef.current.innerHTML);
      updateCompose("body_html", sanitized);
      clearError("body_html");
      clearError("unsubscribe");
    }
  };

  const handleInsertLink = () => {
    const url = window.prompt("Enter URL:", "https://");
    if (url) {
      execCommand("createLink", url);
    }
  };

  const handleInsertVariable = (variable) => {
    execCommand("insertText", variable);
  };

  // Toggle between rich text and HTML mode
  const toggleHtmlMode = () => {
    if (isHtmlMode) {
      // Switching from HTML to Rich Text - set editor content
      setIsHtmlMode(false);
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.innerHTML = campaign.compose.body_html || "";
        }
      }, 0);
    } else {
      // Switching from Rich Text to HTML - content already in state
      setIsHtmlMode(true);
    }
  };

  // Initialize editor when entering step 2
  // Skip if we just did a formatting command (isFormattingRef is true)
  useEffect(() => {
    if (step === 2 && !isHtmlMode && editorRef.current && !isFormattingRef.current) {
      editorRef.current.innerHTML = campaign.compose.body_html || "";
    }
  }, [step, isHtmlMode]);

  // Validate step 1 fields
  const validateStep1 = () => {
    const newErrors = {};
    if (!campaign.name.trim()) {
      newErrors.name = "Campaign name is required";
    }
    if (!campaign.category) {
      newErrors.category = "Category is required";
    }
    if (!campaign.audience.segment.segment) {
      newErrors.segment = "Please select a segment";
    }
    // Check date requirements for date-based segments
    const seg = campaign.audience.segment.segment;
    if (seg === "created_before" && !campaign.audience.segment.created_before_date) {
      newErrors.segment_date = "Please select a date for 'Created Before' segment";
    }
    if (seg === "created_after" && !campaign.audience.segment.created_after_date) {
      newErrors.segment_date = "Please select a date for 'Created After' segment";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate step 2 fields
  const validateStep2 = () => {
    const newErrors = {};
    if (!campaign.compose.subject.trim()) {
      newErrors.subject = "Email subject is required";
    }
    // Use meaningful HTML check instead of simple trim
    if (!isMeaningfulHtml(campaign.compose.body_html)) {
      newErrors.body_html = "Email body is required";
    }
    if (campaign.category === "Marketing" && !campaign.compose.unsubscribe_placeholder_present) {
      newErrors.unsubscribe = "Marketing emails require {{unsubscribe_url}} placeholder in body";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Build proper payload for API
  const buildCampaignPayload = () => {
    // Sanitize and ensure body_html is valid HTML
    let bodyHtml = sanitizeHtml(campaign.compose.body_html.trim());
    if (bodyHtml && !bodyHtml.includes("<") && !bodyHtml.includes(">")) {
      // Plain text - wrap in HTML
      const escaped = bodyHtml
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/\n/g, "<br/>");
      bodyHtml = `<p>${escaped}</p>`;
    }

    return {
      name: campaign.name.trim(),
      category: campaign.category,
      audience: {
        segment: campaign.audience.segment,
        filters: campaign.audience.filters,
      },
      compose: {
        subject: campaign.compose.subject.trim(),
        body_html: bodyHtml,
        unsubscribe_placeholder_present: campaign.compose.unsubscribe_placeholder_present,
      },
    };
  };

  // Create draft campaign
  const createDraft = async () => {
    setLoading(true);
    try {
      const payload = buildCampaignPayload();
      const res = await authFetch(`${API}/admin/tenant-comms/campaigns`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json();
        setCampaignId(data.campaign_id);
        return data.campaign_id;
      } else {
        const err = await res.json();
        // Handle validation errors from backend
        if (err.detail && Array.isArray(err.detail)) {
          const backendErrors = {};
          err.detail.forEach((e) => {
            const field = e.loc?.[1] || "general";
            backendErrors[field] = e.msg;
          });
          setErrors(backendErrors);
          toast.error("Please fix the validation errors");
        } else {
          toast.error(err.detail || "Failed to create campaign");
        }
        return null;
      }
    } catch (err) {
      toast.error("Error creating campaign");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Update existing draft
  const updateDraft = async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const payload = buildCampaignPayload();
      const res = await authFetch(`${API}/admin/tenant-comms/campaigns/${campaignId}`, {
        method: "PUT",
        body: JSON.stringify({
          name: payload.name,
          audience: payload.audience,
          compose: payload.compose,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        if (err.detail && Array.isArray(err.detail)) {
          const backendErrors = {};
          err.detail.forEach((e) => {
            const field = e.loc?.[1] || "general";
            backendErrors[field] = e.msg;
          });
          setErrors(backendErrors);
          toast.error("Please fix the validation errors");
        } else {
          toast.error(err.detail || "Failed to update campaign");
        }
        return false;
      }
      return true;
    } catch (err) {
      toast.error("Error updating campaign");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Preview audience
  const previewAudience = async (id) => {
    const targetId = id || campaignId;
    if (!targetId) return;
    
    setPreviewLoading(true);
    try {
      const res = await authFetch(`${API}/admin/tenant-comms/campaigns/${targetId}/preview`, {
        method: "POST",
      });
      if (res.ok) {
        const data = await res.json();
        setPreview(data);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to preview audience");
      }
    } catch (err) {
      toast.error("Error previewing audience");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Handle step 1 -> 2
  const goToStep2 = async () => {
    if (!validateStep1()) {
      return;
    }
    
    let id = campaignId;
    if (!id) {
      id = await createDraft();
      if (!id) return;
    } else {
      const success = await updateDraft();
      if (!success) return;
    }
    
    await previewAudience(id);
    setStep(2);
  };

  // Handle step 2 -> 3
  const goToStep3 = async () => {
    if (!validateStep2()) {
      return;
    }
    
    const success = await updateDraft();
    if (!success) return;
    
    await previewAudience();
    setStep(3);
  };

  // Send test email
  const handleSendTest = async () => {
    if (!testEmail.trim()) {
      toast.error("Please enter a test email address");
      return;
    }
    
    setSendingTest(true);
    try {
      const res = await authFetch(`${API}/admin/tenant-comms/campaigns/${campaignId}/send-test`, {
        method: "POST",
        body: JSON.stringify({ test_email: testEmail }),
      });
      if (res.ok) {
        toast.success(`Test email sent to ${testEmail}`);
        setShowTestDialog(false);
        setTestEmail("");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to send test email");
      }
    } catch (err) {
      toast.error("Error sending test email");
    } finally {
      setSendingTest(false);
    }
  };

  // Send campaign
  const handleSend = async () => {
    if (confirmText !== "SEND") {
      toast.error("Please type SEND to confirm");
      return;
    }
    
    setSending(true);
    try {
      const res = await authFetch(`${API}/admin/tenant-comms/campaigns/${campaignId}/send`, {
        method: "POST",
        body: JSON.stringify({ confirm_text: "SEND" }),
      });
      if (res.ok) {
        toast.success("Campaign queued for sending!");
        setShowSendDialog(false);
        onComplete(campaignId);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to send campaign");
      }
    } catch (err) {
      toast.error("Error sending campaign");
    } finally {
      setSending(false);
    }
  };

  const selectedSegment = SUPPORTED_SEGMENTS.find((s) => s.value === campaign.audience.segment.segment);

  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-2">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                step === s
                  ? "bg-[#FF6600] text-white"
                  : step > s
                  ? "bg-emerald-500 text-white"
                  : "bg-zinc-800 text-zinc-400"
              }`}
            >
              {step > s ? <CheckCircle size={16} /> : s}
            </div>
            {s < 3 && (
              <div className={`w-12 h-0.5 ${step > s ? "bg-emerald-500" : "bg-zinc-700"}`} />
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-center gap-8 text-sm text-zinc-400">
        <span className={step === 1 ? "text-white font-medium" : ""}>Audience</span>
        <span className={step === 2 ? "text-white font-medium" : ""}>Compose</span>
        <span className={step === 3 ? "text-white font-medium" : ""}>Review & Send</span>
      </div>

      {/* Step 1: Audience */}
      {step === 1 && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users size={20} /> Select Audience
            </CardTitle>
            <CardDescription>Choose which tenants will receive this campaign</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Campaign Name */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Campaign Name *</Label>
              <Input
                value={campaign.name}
                onChange={(e) => { setCampaign((p) => ({ ...p, name: e.target.value })); clearError("name"); }}
                placeholder="e.g., January Product Update"
                className={`bg-zinc-800 border-zinc-700 text-zinc-100 placeholder:text-zinc-500 caret-zinc-100 focus-visible:ring-2 focus-visible:ring-orange-500/60 focus-visible:border-orange-500 ${errors.name ? "border-red-500" : ""}`}
              />
              {errors.name && <p className="text-red-400 text-xs">{errors.name}</p>}
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Category *</Label>
              <Select
                value={campaign.category}
                onValueChange={(v) => { setCampaign((p) => ({ ...p, category: v })); clearError("category"); }}
              >
                <SelectTrigger className={`bg-zinc-800 border-zinc-700 text-zinc-100 focus:ring-2 focus:ring-orange-500/60 ${errors.category ? "border-red-500" : ""}`}>
                  <SelectValue className="text-zinc-400" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Operational">Operational - System updates, important notices</SelectItem>
                  <SelectItem value="Product">Product - Feature announcements, tips</SelectItem>
                  <SelectItem value="Marketing">Marketing - Promotions (requires unsubscribe)</SelectItem>
                </SelectContent>
              </Select>
              {errors.category && <p className="text-red-400 text-xs">{errors.category}</p>}
            </div>

            {/* Segment Selection */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Segment *</Label>
              <div className="grid gap-2">
                {SUPPORTED_SEGMENTS.map((seg) => {
                  const Icon = seg.icon;
                  const isSelected = campaign.audience.segment.segment === seg.value;
                  return (
                    <button
                      key={seg.value}
                      type="button"
                      onClick={() => updateSegment("segment", seg.value)}
                      className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-colors ${
                        isSelected
                          ? "border-[#FF6600] bg-[#FF6600]/10"
                          : "border-zinc-700 bg-zinc-800/50 hover:border-zinc-600"
                      }`}
                    >
                      <Icon size={20} className={isSelected ? "text-[#FF6600]" : "text-zinc-400"} />
                      <div>
                        <p className={`font-medium ${isSelected ? "text-white" : "text-zinc-300"}`}>
                          {seg.label}
                        </p>
                        <p className="text-xs text-zinc-500">{seg.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Date fields for date-based segments */}
            {selectedSegment?.requiresDate && (
              <div className="space-y-2">
                <Label className="text-zinc-300">
                  {selectedSegment.requiresDate === "created_before_date" ? "Created Before" : "Created After"} *
                </Label>
                <Input
                  type="date"
                  value={campaign.audience.segment[selectedSegment.requiresDate]?.split("T")[0] || ""}
                  onChange={(e) => { updateSegment(selectedSegment.requiresDate, e.target.value ? `${e.target.value}T00:00:00Z` : ""); clearError("segment_date"); }}
                  className={`bg-zinc-800 border-zinc-700 text-zinc-100 placeholder:text-zinc-500 caret-zinc-100 focus-visible:ring-2 focus-visible:ring-orange-500/60 focus-visible:border-orange-500 [color-scheme:dark] ${errors.segment_date ? "border-red-500" : ""}`}
                />
                {errors.segment_date && <p className="text-red-400 text-xs">{errors.segment_date}</p>}
              </div>
            )}

            {/* Optional Filters */}
            <div className="space-y-2 pt-4 border-t border-zinc-800">
              <Label className="text-zinc-300 flex items-center gap-2">
                <Funnel size={16} /> Additional Filters (Optional)
              </Label>
              
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs text-zinc-500">Status Filter</Label>
                  <Select
                    value={campaign.audience.filters?.status || "__all__"}
                    onValueChange={(v) => updateFilters("status", v === "__all__" ? "" : v)}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100 focus:ring-2 focus:ring-orange-500/60">
                      <SelectValue placeholder="All statuses" className="text-zinc-400" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All statuses</SelectItem>
                      <SelectItem value="active">Active only</SelectItem>
                      <SelectItem value="suspended">Suspended only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Compose */}
      {step === 2 && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Envelope size={20} /> Compose Email
            </CardTitle>
            <CardDescription>Write your campaign email content</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Template Selection */}
            {availableTemplates.length > 0 && (
              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText size={18} className="text-[#FF6600]" />
                    <span className="text-zinc-300 text-sm font-medium">Start from a template</span>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowTemplateSelector(!showTemplateSelector)}
                    className="border-zinc-600 text-zinc-300 hover:bg-zinc-700 hover:text-white"
                  >
                    {showTemplateSelector ? "Hide Templates" : "Use Template"}
                  </Button>
                </div>
                {showTemplateSelector && (
                  <div className="mt-4 grid gap-2 max-h-48 overflow-y-auto">
                    {availableTemplates.map((tmpl) => (
                      <button
                        key={tmpl.template_id}
                        type="button"
                        onClick={() => applyTemplate(tmpl)}
                        className="flex items-center justify-between p-3 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-left transition-colors"
                      >
                        <div>
                          <p className="text-zinc-100 font-medium">{tmpl.name}</p>
                          {tmpl.description && <p className="text-zinc-400 text-xs mt-0.5">{tmpl.description}</p>}
                        </div>
                        <span className="text-xs text-zinc-500 truncate max-w-xs">{tmpl.subject}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Preview info */}
            {preview && (
              <div className="p-3 rounded-lg bg-zinc-800 border border-zinc-700">
                <p className="text-sm text-zinc-300">
                  <Users size={16} className="inline mr-2" />
                  Estimated recipients: <span className="font-semibold text-white">{preview.estimated_count}</span>
                </p>
              </div>
            )}

            {/* Subject */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Subject Line *</Label>
              <Input
                value={campaign.compose.subject}
                onChange={(e) => { updateCompose("subject", e.target.value); clearError("subject"); }}
                placeholder="e.g., Important: New Features Available"
                className={`bg-zinc-800 border-zinc-700 text-zinc-100 placeholder:text-zinc-500 caret-zinc-100 focus-visible:ring-2 focus-visible:ring-orange-500/60 focus-visible:border-orange-500 ${errors.subject ? "border-red-500" : ""}`}
                maxLength={200}
              />
              {errors.subject && <p className="text-red-400 text-xs">{errors.subject}</p>}
              <p className="text-xs text-zinc-500">{campaign.compose.subject.length}/200 characters</p>
            </div>

            {/* Body with Rich Text / HTML toggle */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-zinc-300">Email Body *</Label>
                <div className="flex items-center gap-1 bg-zinc-800 rounded-md p-0.5">
                  <button
                    type="button"
                    onClick={() => !isHtmlMode || toggleHtmlMode()}
                    className={`px-2 py-1 text-xs rounded transition-colors ${!isHtmlMode ? "bg-[#FF6600] text-white" : "text-zinc-400 hover:text-white"}`}
                  >
                    Rich Text
                  </button>
                  <button
                    type="button"
                    onClick={() => isHtmlMode || toggleHtmlMode()}
                    className={`px-2 py-1 text-xs rounded transition-colors ${isHtmlMode ? "bg-[#FF6600] text-white" : "text-zinc-400 hover:text-white"}`}
                  >
                    <Code size={14} className="inline mr-1" />
                    HTML
                  </button>
                </div>
              </div>

              {/* Rich Text Editor */}
              {!isHtmlMode && (
                <div className={`border rounded-lg overflow-hidden ${errors.body_html ? "border-red-500" : "border-zinc-700"}`}>
                  {/* Toolbar */}
                  <div className="flex flex-wrap items-center gap-0.5 p-2 bg-zinc-800 border-b border-zinc-700">
                    <button
                      type="button"
                      onClick={() => execCommand("bold")}
                      className="p-1.5 rounded hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors"
                      title="Bold (Ctrl+B)"
                    >
                      <TextBolder size={18} weight="bold" />
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand("italic")}
                      className="p-1.5 rounded hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors"
                      title="Italic (Ctrl+I)"
                    >
                      <TextItalic size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand("underline")}
                      className="p-1.5 rounded hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors"
                      title="Underline (Ctrl+U)"
                    >
                      <TextUnderline size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand("strikeThrough")}
                      className="p-1.5 rounded hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors"
                      title="Strikethrough"
                    >
                      <TextStrikethrough size={18} />
                    </button>
                    <div className="w-px h-5 bg-zinc-600 mx-1" />
                    <button
                      type="button"
                      onClick={() => execCommand("insertUnorderedList")}
                      className="p-1.5 rounded hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors"
                      title="Bullet List"
                    >
                      <ListBullets size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand("insertOrderedList")}
                      className="p-1.5 rounded hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors"
                      title="Numbered List"
                    >
                      <ListNumbers size={18} />
                    </button>
                    <div className="w-px h-5 bg-zinc-600 mx-1" />
                    <button
                      type="button"
                      onClick={handleInsertLink}
                      className="p-1.5 rounded hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors"
                      title="Insert Link"
                    >
                      <Link size={18} />
                    </button>
                    <div className="w-px h-5 bg-zinc-600 mx-1" />
                    <button
                      type="button"
                      onClick={() => execCommand("undo")}
                      className="p-1.5 rounded hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors"
                      title="Undo (Ctrl+Z)"
                    >
                      <ArrowUUpLeft size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand("redo")}
                      className="p-1.5 rounded hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors"
                      title="Redo (Ctrl+Y)"
                    >
                      <ArrowUUpRight size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand("removeFormat")}
                      className="p-1.5 rounded hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors"
                      title="Remove Formatting"
                    >
                      <X size={18} />
                    </button>
                    <div className="flex-1" />
                    {/* Variable insert buttons */}
                    <button
                      type="button"
                      onClick={() => handleInsertVariable("{{tenant_name}}")}
                      className="px-2 py-1 text-xs rounded bg-zinc-700 hover:bg-zinc-600 text-zinc-300 hover:text-white transition-colors"
                      title="Insert tenant name"
                    >
                      {"{{tenant_name}}"}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleInsertVariable("{{owner_name}}")}
                      className="px-2 py-1 text-xs rounded bg-zinc-700 hover:bg-zinc-600 text-zinc-300 hover:text-white transition-colors"
                      title="Insert owner name"
                    >
                      {"{{owner_name}}"}
                    </button>
                    {campaign.category === "Marketing" && (
                      <button
                        type="button"
                        onClick={() => handleInsertVariable("{{unsubscribe_url}}")}
                        className="px-2 py-1 text-xs rounded bg-pink-600/30 hover:bg-pink-600/50 text-pink-400 hover:text-pink-300 transition-colors"
                        title="Insert unsubscribe link (required for Marketing)"
                      >
                        {"{{unsubscribe_url}}"}
                      </button>
                    )}
                  </div>
                  {/* Editor Surface */}
                  <div
                    ref={editorRef}
                    contentEditable
                    onInput={handleEditorInput}
                    className="min-h-[200px] p-4 bg-zinc-900 text-zinc-100 focus:outline-none prose prose-invert prose-sm max-w-none"
                    style={{ caretColor: "#f4f4f5" }}
                    data-placeholder="Start typing your email content..."
                  />
                </div>
              )}

              {/* HTML Textarea (when in HTML mode) */}
              {isHtmlMode && (
                <Textarea
                  value={campaign.compose.body_html}
                  onChange={(e) => { updateCompose("body_html", e.target.value); clearError("body_html"); clearError("unsubscribe"); }}
                  placeholder="<p>Hello {{tenant_name}},</p><p>We have exciting news...</p>"
                  className={`bg-zinc-800 border-zinc-700 min-h-[200px] font-mono text-sm text-zinc-100 placeholder:text-zinc-500 caret-zinc-100 focus-visible:ring-2 focus-visible:ring-orange-500/60 focus-visible:border-orange-500 ${errors.body_html ? "border-red-500" : ""}`}
                />
              )}
              
              {errors.body_html && <p className="text-red-400 text-xs">{errors.body_html}</p>}
              
              {/* Variables hint (shown in HTML mode) */}
              {isHtmlMode && (
                <div className="flex flex-wrap gap-2 text-xs text-zinc-500">
                  <span>Available variables:</span>
                  <code className="px-1 bg-zinc-800 rounded">{"{{tenant_name}}"}</code>
                  <code className="px-1 bg-zinc-800 rounded">{"{{owner_name}}"}</code>
                  {campaign.category === "Marketing" && (
                    <code className="px-1 bg-zinc-800 rounded text-pink-400">{"{{unsubscribe_url}}"}</code>
                  )}
                </div>
              )}
            </div>

            {/* Marketing warning */}
            {(campaign.category === "Marketing" && !campaign.compose.unsubscribe_placeholder_present) || errors.unsubscribe ? (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                <Warning size={16} className="inline mr-2" />
                {errors.unsubscribe || "Marketing emails require an unsubscribe link. Add {{unsubscribe_url}} to your email body."}
              </div>
            ) : null}

            {/* Preview pane */}
            {isMeaningfulHtml(campaign.compose.body_html) && (
              <div className="space-y-2">
                <Label className="text-zinc-300">Preview</Label>
                <div
                  className="p-4 rounded-lg bg-white text-zinc-900 text-sm prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: campaign.compose.body_html
                      .replace(/\{\{tenant_name\}\}/g, "Acme Services")
                      .replace(/\{\{owner_name\}\}/g, "John Smith")
                      .replace(/\{\{unsubscribe_url\}\}/g, "#"),
                  }}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 3: Review & Send */}
      {step === 3 && (
        <div className="space-y-6">
          {/* Error display */}
          {Object.keys(errors).length > 0 && (
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
              <p className="text-red-400 font-medium mb-2">Please fix the following errors:</p>
              <ul className="list-disc list-inside text-red-400 text-sm space-y-1">
                {Object.entries(errors).map(([field, msg]) => (
                  <li key={field}>{msg}</li>
                ))}
              </ul>
            </div>
          )}
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Eye size={20} /> Review Campaign
              </CardTitle>
              <CardDescription>Review your campaign before sending</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Campaign Summary */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <p className="text-xs text-zinc-500">Campaign Name</p>
                  <p className="text-white font-medium">{campaign.name}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-zinc-500">Category</p>
                  <Badge variant="outline" className={`${CATEGORY_CONFIG[campaign.category]?.color}`}>
                    {campaign.category}
                  </Badge>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-zinc-500">Segment</p>
                  <p className="text-white">{selectedSegment?.label}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-zinc-500">Estimated Recipients</p>
                  <p className="text-white font-semibold text-lg">{preview?.estimated_count || 0}</p>
                </div>
              </div>

              {/* Subject Preview */}
              <div className="pt-4 border-t border-zinc-800">
                <p className="text-xs text-zinc-500 mb-1">Subject</p>
                <p className="text-white">{campaign.compose.subject}</p>
              </div>

              {/* Content Preview */}
              <div className="pt-4 border-t border-zinc-800">
                <p className="text-xs text-zinc-500 mb-2">Content Preview</p>
                <div
                  className="p-4 rounded-lg bg-white text-zinc-900 text-sm prose prose-sm max-w-none max-h-[200px] overflow-y-auto"
                  dangerouslySetInnerHTML={{
                    __html: campaign.compose.body_html
                      .replace(/\{\{tenant_name\}\}/g, "Acme Services")
                      .replace(/\{\{owner_name\}\}/g, "John Smith")
                      .replace(/\{\{unsubscribe_url\}\}/g, "#"),
                  }}
                />
              </div>

              {/* Sample Recipients */}
              {preview?.sample_tenants?.length > 0 && (
                <div className="pt-4 border-t border-zinc-800">
                  <p className="text-xs text-zinc-500 mb-2">Sample Recipients (first {preview.sample_tenants.length})</p>
                  <div className="space-y-1 text-sm">
                    {preview.sample_tenants.slice(0, 5).map((t, i) => (
                      <div key={i} className="flex items-center justify-between text-zinc-300">
                        <span>{t.company_name || t.tenant_id}</span>
                        <span className="text-zinc-500">{t.owner_email}</span>
                      </div>
                    ))}
                    {preview.sample_tenants.length > 5 && (
                      <p className="text-zinc-500">...and {preview.estimated_count - 5} more</p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowTestDialog(true)}
                  className="border-zinc-700"
                  disabled={!campaignId || !campaign.compose.subject.trim() || !campaign.compose.body_html.trim()}
                >
                  <TestTube size={16} className="mr-2" /> Send Test Email
                </Button>
                <Button
                  onClick={() => setShowSendDialog(true)}
                  className="bg-[#FF6600] hover:bg-[#FF6600]/90 flex-1 sm:flex-none"
                  disabled={!campaignId || !preview?.estimated_count || !campaign.compose.subject.trim() || !campaign.compose.body_html.trim()}
                >
                  <PaperPlaneTilt size={16} className="mr-2" /> Send to {preview?.estimated_count || 0} Recipients
                </Button>
              </div>
              {!campaignId && (
                <p className="text-yellow-400 text-xs mt-2">Campaign must be saved before sending</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
        <Button
          variant="outline"
          onClick={step === 1 ? onCancel : () => setStep((s) => s - 1)}
          className="border-zinc-700"
        >
          <ArrowLeft size={16} className="mr-2" /> {step === 1 ? "Cancel" : "Back"}
        </Button>
        {step < 3 && (
          <Button
            onClick={step === 1 ? goToStep2 : goToStep3}
            disabled={loading}
            className="bg-[#FF6600] hover:bg-[#FF6600]/90"
          >
            {loading ? "Saving..." : "Next"} <ArrowRight size={16} className="ml-2" />
          </Button>
        )}
      </div>

      {/* Send Test Dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Send Test Email</DialogTitle>
            <DialogDescription>
              Send a test email to preview how the campaign will look.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Test Email Address</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="your@email.com"
                className="bg-zinc-800 border-zinc-700 text-zinc-100 placeholder:text-zinc-500 caret-zinc-100 focus-visible:ring-2 focus-visible:ring-orange-500/60 focus-visible:border-orange-500"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestDialog(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleSendTest} disabled={sendingTest} className="bg-[#FF6600] hover:bg-[#FF6600]/90">
              {sendingTest ? "Sending..." : "Send Test"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Confirmation Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Confirm Send</DialogTitle>
            <DialogDescription>
              You are about to send this campaign to <span className="font-semibold text-white">{preview?.estimated_count || 0}</span> recipients.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-sm">
              <Warning size={16} className="inline mr-2" />
              Type <span className="font-bold">SEND</span> to confirm sending this campaign.
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Confirmation</Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                placeholder="Type SEND"
                className="bg-zinc-800 border-zinc-700 font-mono text-zinc-100 placeholder:text-zinc-500 caret-zinc-100 focus-visible:ring-2 focus-visible:ring-orange-500/60 focus-visible:border-orange-500"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending || confirmText !== "SEND"}
              className="bg-[#FF6600] hover:bg-[#FF6600]/90"
            >
              {sending ? "Sending..." : "Send Campaign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// CAMPAIGN DETAIL VIEW
// ============================================================================

function CampaignDetailView({ campaignId, onBack }) {
  const [campaign, setCampaign] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const fetchCampaign = useCallback(async () => {
    setLoading(true);
    try {
      const [campaignRes, resultsRes] = await Promise.all([
        authFetch(`${API}/admin/tenant-comms/campaigns/${campaignId}`),
        authFetch(`${API}/admin/tenant-comms/campaigns/${campaignId}/results`),
      ]);
      
      if (campaignRes.ok) {
        setCampaign(await campaignRes.json());
      }
      if (resultsRes.ok) {
        setResults(await resultsRes.json());
      }
    } catch (err) {
      toast.error("Error loading campaign details");
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    fetchCampaign();
  }, [fetchCampaign]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await authFetch(`${API}/admin/tenant-comms/campaigns/${campaignId}/export`);
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `campaign_${campaignId.slice(0, 8)}_results.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Export downloaded");
      } else {
        toast.error("Failed to export results");
      }
    } catch (err) {
      toast.error("Error exporting results");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return <div className="text-center text-zinc-400 py-8">Loading campaign details...</div>;
  }

  if (!campaign) {
    return <div className="text-center text-zinc-400 py-8">Campaign not found</div>;
  }

  const StatusIcon = STATUS_CONFIG[campaign.status]?.icon || Clock;
  const counts = campaign.resolved_counts || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={onBack} className="border-zinc-700">
            <ArrowLeft size={16} className="mr-2" /> Back
          </Button>
          <div>
            <h2 className="text-lg font-semibold text-white">{campaign.name || "(Untitled)"}</h2>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className={`${CATEGORY_CONFIG[campaign.category]?.color || ""} text-xs`}>
                {campaign.category}
              </Badge>
              <Badge variant="outline" className={`${STATUS_CONFIG[campaign.status]?.color || ""} text-xs`}>
                <StatusIcon size={12} className="mr-1" />
                {STATUS_CONFIG[campaign.status]?.label || campaign.status}
              </Badge>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={fetchCampaign} variant="outline" size="sm" className="border-zinc-700">
            <ArrowClockwise size={16} className="mr-1" /> Refresh
          </Button>
          <Button onClick={handleExport} disabled={exporting} size="sm" className="bg-[#FF6600] hover:bg-[#FF6600]/90">
            <Download size={16} className="mr-1" /> {exporting ? "Exporting..." : "Export CSV"}
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-zinc-500 mb-1">Estimated</p>
            <p className="text-2xl font-semibold text-white">{counts.estimated || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-zinc-500 mb-1">Attempted</p>
            <p className="text-2xl font-semibold text-blue-400">{counts.attempted || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-zinc-500 mb-1">Delivered</p>
            <p className="text-2xl font-semibold text-emerald-400">{counts.delivered || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-zinc-500 mb-1">Failed</p>
            <p className="text-2xl font-semibold text-red-400">{counts.failed || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-zinc-500 mb-1">Suppressed</p>
            <p className="text-2xl font-semibold text-zinc-400">{counts.suppressed || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Timestamps */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-zinc-400">Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-4 text-sm">
            <div>
              <p className="text-zinc-500">Created</p>
              <p className="text-white">{formatDate(campaign.created_at)}</p>
            </div>
            <div>
              <p className="text-zinc-500">Send Requested</p>
              <p className="text-white">{formatDate(campaign.send_requested_at)}</p>
            </div>
            <div>
              <p className="text-zinc-500">Send Started</p>
              <p className="text-white">{formatDate(campaign.send_started_at)}</p>
            </div>
            <div>
              <p className="text-zinc-500">Completed</p>
              <p className="text-white">{formatDate(campaign.send_completed_at)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Message */}
      {campaign.last_error && (
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="p-4">
            <p className="text-sm text-red-400">
              <Warning size={16} className="inline mr-2" />
              Error: {campaign.last_error}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Ledger Status Breakdown */}
      {results?.ledger_status_breakdown && Object.keys(results.ledger_status_breakdown).length > 0 && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-zinc-400">Status Breakdown (from ledger)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(results.ledger_status_breakdown).map(([status, count]) => (
                <Badge
                  key={status}
                  variant="outline"
                  className={`${STATUS_CONFIG[status]?.color || "bg-zinc-500/20 text-zinc-400 border-zinc-500/30"}`}
                >
                  {status}: {count}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Campaign Content Preview */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-zinc-400">Email Content</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-xs text-zinc-500 mb-1">Subject</p>
            <p className="text-white">{campaign.compose?.subject}</p>
          </div>
          <div>
            <p className="text-xs text-zinc-500 mb-2">Body</p>
            <div
              className="p-4 rounded-lg bg-white text-zinc-900 text-sm prose prose-sm max-w-none max-h-[300px] overflow-y-auto"
              dangerouslySetInnerHTML={{ __html: campaign.compose?.body_html || "<p>No content</p>" }}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// SEGMENTS TAB (View Only)
// ============================================================================

function SegmentsTab() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-white">Supported Segments</h2>
        <p className="text-sm text-zinc-400">These segments are available for tenant broadcast campaigns</p>
      </div>

      <div className="grid gap-4">
        {SUPPORTED_SEGMENTS.map((seg) => {
          const Icon = seg.icon;
          return (
            <Card key={seg.value} className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4 flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#FF6600]/20 flex items-center justify-center">
                  <Icon size={20} className="text-[#FF6600]" />
                </div>
                <div>
                  <h3 className="text-white font-medium">{seg.label}</h3>
                  <p className="text-sm text-zinc-400">{seg.description}</p>
                  <p className="text-xs text-zinc-500 mt-1 font-mono">segment: {seg.value}</p>
                  {seg.requiresDate && (
                    <p className="text-xs text-yellow-500 mt-1">
                      <Info size={12} className="inline mr-1" />
                      Requires: {seg.requiresDate}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="bg-zinc-800/50 border-zinc-700">
        <CardContent className="p-4">
          <p className="text-sm text-zinc-400">
            <Info size={16} className="inline mr-2" />
            Additional segments may be added in future updates. Contact engineering to request new segment types.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// SUPPRESSION TAB (View Only)
// ============================================================================

function SuppressionTab() {
  const [suppressions, setSuppressions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasEndpoint, setHasEndpoint] = useState(true);

  useEffect(() => {
    // Note: Backend suppression list endpoint may not exist yet
    // For now, show placeholder
    setLoading(false);
    setHasEndpoint(false);
  }, []);

  if (!hasEndpoint) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-white">Suppression List</h2>
          <p className="text-sm text-zinc-400">View recipients who are suppressed from broadcast emails</p>
        </div>

        <Card className="bg-zinc-800/50 border-zinc-700">
          <CardContent className="p-8 text-center">
            <UserMinus size={48} className="mx-auto mb-4 text-zinc-500" />
            <p className="text-zinc-400">Suppression viewer not yet wired</p>
            <p className="text-sm text-zinc-500 mt-2">
              The suppression list backend endpoint is not yet exposed for admin viewing.
              <br />
              Suppression is enforced automatically for admin_broadcast emails.
            </p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-sm text-zinc-400">How Suppression Works</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-zinc-300">
            <p>• Recipients can be suppressed per channel (email, SMS)</p>
            <p>• Suppression is <span className="text-white font-medium">HARD enforced</span> for admin_broadcast source</p>
            <p>• Transactional emails are <span className="text-white font-medium">never suppressed</span></p>
            <p>• Suppression entries are stored in the tenant_comms_suppression collection</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-white">Suppression List</h2>
        <p className="text-sm text-zinc-400">View recipients who are suppressed from broadcast emails</p>
      </div>

      {loading ? (
        <div className="text-center text-zinc-400 py-8">Loading suppression list...</div>
      ) : suppressions.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-8 text-center">
            <UserMinus size={48} className="mx-auto mb-4 text-zinc-500" />
            <p className="text-zinc-400">No suppressions found</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800 text-zinc-400">
                  <th className="text-left p-3 font-medium">Recipient</th>
                  <th className="text-left p-3 font-medium">Channel</th>
                  <th className="text-left p-3 font-medium">Reason</th>
                  <th className="text-left p-3 font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {suppressions.map((s, i) => (
                  <tr key={i} className="border-b border-zinc-800/50">
                    <td className="p-3 text-white">{s.recipient}</td>
                    <td className="p-3 text-zinc-300">{s.channel}</td>
                    <td className="p-3 text-zinc-300">{s.reason}</td>
                    <td className="p-3 text-zinc-400">{formatDate(s.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export default function AdminTenantComms() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const initialTab = searchParams.get("tab") || "campaigns";
  const campaignIdFromUrl = searchParams.get("campaign");
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedCampaignId, setSelectedCampaignId] = useState(campaignIdFromUrl);
  const [isCreating, setIsCreating] = useState(false);

  // Update URL when tab changes
  const handleTabChange = (value) => {
    setActiveTab(value);
    setSelectedCampaignId(null);
    setIsCreating(false);
    if (value === "campaigns") {
      setSearchParams({});
    } else {
      setSearchParams({ tab: value });
    }
  };

  // View campaign details
  const handleViewCampaign = (campaignId) => {
    setSelectedCampaignId(campaignId);
    setSearchParams({ campaign: campaignId });
  };

  // Back to campaigns list
  const handleBackToList = () => {
    setSelectedCampaignId(null);
    setSearchParams({});
  };

  // Start creating campaign
  const handleCreateCampaign = () => {
    setIsCreating(true);
    setSearchParams({ tab: "create" });
  };

  // Campaign creation complete
  const handleCampaignCreated = (campaignId) => {
    setIsCreating(false);
    handleViewCampaign(campaignId);
  };

  // Cancel creation
  const handleCancelCreate = () => {
    setIsCreating(false);
    setSearchParams({});
    setActiveTab("campaigns");
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <Megaphone size={28} className="text-[#FF6600]" />
          Tenant Communications
        </h1>
        <p className="text-zinc-400 mt-1">
          Broadcast campaigns to tenant segments
        </p>
      </div>

      {/* Main Content */}
      {selectedCampaignId ? (
        <CampaignDetailView campaignId={selectedCampaignId} onBack={handleBackToList} />
      ) : isCreating ? (
        <CreateCampaignWizard onComplete={handleCampaignCreated} onCancel={handleCancelCreate} />
      ) : (
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="bg-zinc-800 border border-zinc-700">
            <TabsTrigger value="campaigns" className="data-[state=active]:bg-[#FF6600]">
              <Megaphone size={16} className="mr-2" /> Campaigns
            </TabsTrigger>
            <TabsTrigger value="segments" className="data-[state=active]:bg-[#FF6600]">
              <ListBullets size={16} className="mr-2" /> Segments
            </TabsTrigger>
            <TabsTrigger value="suppression" className="data-[state=active]:bg-[#FF6600]">
              <UserMinus size={16} className="mr-2" /> Suppression
            </TabsTrigger>
            <TabsTrigger value="automations" className="data-[state=active]:bg-[#FF6600]">
              <Robot size={16} className="mr-2" /> Automations
            </TabsTrigger>
            <TabsTrigger value="templates" className="data-[state=active]:bg-[#FF6600]">
              <FileText size={16} className="mr-2" /> Templates
            </TabsTrigger>
          </TabsList>

          <TabsContent value="campaigns" className="mt-6">
            <CampaignsListTab onViewCampaign={handleViewCampaign} onCreateCampaign={handleCreateCampaign} />
          </TabsContent>

          <TabsContent value="segments" className="mt-6">
            <SegmentsTab />
          </TabsContent>

          <TabsContent value="suppression" className="mt-6">
            <SuppressionTab />
          </TabsContent>

          <TabsContent value="automations" className="mt-6">
            <AutomationsTab />
          </TabsContent>

          <TabsContent value="templates" className="mt-6">
            <TemplatesTab />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
